/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { computed, inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { APP_VERSION } from './app-version';
import { ChangelogDialogComponent } from './changelog-dialog.component';

/** The version whose changelog this browser last opened. */
const SEEN_KEY = 'aca.changelog.seen';

/**
 * "What's new": the changelog dialog, and whether this browser has read the
 * current version's entry. The help button carries a dot until it has, so an
 * update is noticed without a pop-up interrupting anyone's work.
 *
 * Kept in the browser: it is a convenience, and a lost value only shows the
 * dot once more.
 */
@Injectable({ providedIn: 'root' })
export class WhatsNewService {
  private readonly dialog = inject(MatDialog);

  private readonly seen = signal(WhatsNewService.read());

  readonly unseen = computed(() => this.seen() !== APP_VERSION);

  open(): void {
    this.dialog.open(ChangelogDialogComponent, {
      width: '540px',
      maxWidth: '94vw',
      autoFocus: false,
      panelClass: 'aca-changelog-panel'
    });
    this.markSeen();
  }

  markSeen(): void {
    this.seen.set(APP_VERSION);
    try {
      localStorage.setItem(SEEN_KEY, APP_VERSION);
    } catch {
      // storage refused: the dot just comes back next visit
    }
  }

  private static read(): string {
    try {
      return localStorage.getItem(SEEN_KEY) ?? '';
    } catch {
      return '';
    }
  }
}
